/**
 * 操作紀錄事件名稱中文對照
 */
export const LOG_EVENT_LABELS = {
    disabled: '停用',
    disable: '停用',
    created: '建立',
    create: '建立',
    grant: '發放',
    granted: '發放',
    transfer_in: '轉入',
    transfer_out: '轉出',
    update: '更新',
    updated: '更新',
    use: '使用',
    used: '使用',
    expired: '過期',
    expire: '過期',
    course_completed: '完課'
};

export const formatLogEvent = (event) => LOG_EVENT_LABELS[event] ?? event;

/**
 * 從 old/new values 中提取「有 _label 對應」的欄位列表
 * 例如：{ completion_status: 'none', completion_status_label: '準備上課' }
 * → 顯示 completion_status，值取 completion_status_label
 */
const extractLabeledKeys = (oldValues, newValues) => {
    const allKeys = new Set([
        ...Object.keys(oldValues ?? {}),
        ...Object.keys(newValues ?? {})
    ]);
    // 只保留在任意一邊有 {key}_label 存在的 key（且本身不是 _label 結尾的輔助欄位）
    return [...allKeys].filter((key) => {
        if (key.endsWith('_label')) return false;
        return allKeys.has(`${key}_label`);
    });
};

/**
 * 欄位顯示名稱（fallback，若後端無 _label 也能顯示）
 */
const INVENTORY_FIELD_LABELS = {
    status: '物品狀態',
    memo: '備註',
    expires_at: '到期時間'
};

const SUBMISSION_FIELD_LABELS = {
    completion_status: '完課狀態',
    session_id: '場次',
    session_name: '場次名稱'
};

/**
 * 比對 old_values / new_values，回傳 diff 陣列
 * 只包含有 _label 對應的欄位，值優先使用 {key}_label
 */
export const buildLogDiff = (oldValues, newValues, fieldLabels) => {
    const keys = extractLabeledKeys(oldValues, newValues);
    return keys.map((key) => {
        const labelKey = `${key}_label`;
        const oldVal = oldValues?.[labelKey] ?? oldValues?.[key];
        const newVal = newValues?.[labelKey] ?? newValues?.[key];
        return {
            key,
            label: fieldLabels[key] ?? key,
            oldVal: oldVal ?? '（空）',
            newVal: newVal ?? '（空）',
            hasOld: oldValues != null && key in (oldValues ?? {}),
            hasNew: newValues != null && key in (newValues ?? {})
        };
    });
};

/** 背包物品紀錄 diff */
export const buildInventoryLogDiff = (old_values, new_values) =>
    buildLogDiff(old_values, new_values, INVENTORY_FIELD_LABELS);

/** 表單提交紀錄 diff */
export const buildSubmissionLogDiff = (old_values, new_values) =>
    buildLogDiff(old_values, new_values, SUBMISSION_FIELD_LABELS);

const formatAuditValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '（空）';
    }
    if (typeof value === 'boolean') {
        return value ? '是' : '否';
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
};

/**
 * 解析後端 display 結構 { label, text, raw } 或 fallback 原始值
 */
const parseAuditDisplayEntry = (entry, rawFallback) => {
    if (entry && typeof entry === 'object' && 'text' in entry) {
        if (entry.masked) {
            return {
                label: entry.label || null,
                text: formatAuditValue(entry.text),
                raw: null,
                showRaw: false,
                masked: true
            };
        }
        const text = formatAuditValue(entry.text);
        const raw = formatAuditValue(
            entry.raw !== undefined && entry.raw !== null
                ? entry.raw
                : rawFallback
        );
        return {
            label: entry.label || null,
            text,
            raw,
            showRaw: text !== raw,
            masked: false
        };
    }
    const text = formatAuditValue(entry ?? rawFallback);
    return { label: null, text, raw: text, showRaw: false, masked: false };
};

/**
 * 組合操作稽核原始 JSON（供底部收合區塊使用）
 */
export const buildAuditRawPayload = (oldValues, newValues) => {
    const payload = {};
    if (oldValues && Object.keys(oldValues).length > 0) {
        payload.old_values = oldValues;
    }
    if (newValues && Object.keys(newValues).length > 0) {
        payload.new_values = newValues;
    }
    if (Object.keys(payload).length === 0) {
        return null;
    }
    return payload;
};

export const formatAuditRawJson = (oldValues, newValues) => {
    const payload = buildAuditRawPayload(oldValues, newValues);
    if (!payload) return null;
    try {
        return JSON.stringify(payload, null, 2);
    } catch {
        return null;
    }
};

/**
 * 操作稽核（admin_audit_logs）diff
 * 主顯示譯文（text）；原始值請看底部 JSON 收合區
 */
export const buildAuditLogDiff = (
    oldValues,
    newValues,
    oldDisplay,
    newDisplay
) => {
    const rawKeys = new Set([
        ...Object.keys(oldValues ?? {}),
        ...Object.keys(newValues ?? {})
    ]);
    const displayKeys = new Set([
        ...Object.keys(oldDisplay ?? {}),
        ...Object.keys(newDisplay ?? {})
    ]);
    const keys = rawKeys.size > 0 ? rawKeys : displayKeys;

    return [...keys].map((key) => {
        const hasOld =
            Object.prototype.hasOwnProperty.call(oldValues ?? {}, key) ||
            Object.prototype.hasOwnProperty.call(oldDisplay ?? {}, key);
        const hasNew =
            Object.prototype.hasOwnProperty.call(newValues ?? {}, key) ||
            Object.prototype.hasOwnProperty.call(newDisplay ?? {}, key);

        const oldParsed = parseAuditDisplayEntry(
            oldDisplay?.[key],
            oldValues?.[key]
        );
        const newParsed = parseAuditDisplayEntry(
            newDisplay?.[key],
            newValues?.[key]
        );

        const masked = Boolean(newParsed.masked || oldParsed.masked);

        return {
            key,
            label: newParsed.label || oldParsed.label || key,
            oldVal: oldParsed.text,
            oldRaw: oldParsed.raw,
            showOldRaw: oldParsed.showRaw,
            newVal: newParsed.text,
            newRaw: newParsed.raw,
            showNewRaw: newParsed.showRaw,
            masked,
            hasOld,
            hasNew,
            changed: masked || oldParsed.text !== newParsed.text
        };
    });
};

export const getAuditActionTagColor = (action) => {
    const map = {
        created: 'success',
        updated: 'processing',
        deleted: 'error',
        settled: 'purple',
        imported: 'cyan'
    };
    return map[action] || 'default';
};
